'use client';

import React from 'react';
import { AppImage } from '@/components/common/AppImage';
import { Card } from '@/components/common/Card';
import { User, Users, Wallet } from 'lucide-react';
import { formatCurrency } from '@/lib/utils/format';
import { cn } from '@/lib/utils';
import { TeamActions } from './TeamActions';
import type { Team, Tournament } from '@/lib/types/auction';
import { useAuth } from '@/hooks/useAuth';

interface TeamCardProps {
  team: Team;
  tournament?: Tournament;
  onClick?: () => void;
  onAction?: () => void;
  onEdit?: (team: Team) => void;
}

export function TeamCard({
  team,
  tournament,
  onClick,
  onAction,
  onEdit,
}: TeamCardProps) {
  const { id, name, logo, playerCount, budget, spent, owner } = team;
  const { user } = useAuth();

  const canManageTeam = React.useMemo(() => {
    if (!user) return false;
    const isTeamOwner = team?.owner?.id === user?.id;
    const tournamentOwner = tournament?.owner?.id || team.tournament?.owner?.id;
    const isTournamentOwner = tournamentOwner === user?.id;
    return isTeamOwner || isTournamentOwner;
  }, [team, tournament, user]);

  return (
    <Card
      className={cn(
        'group relative flex h-full cursor-pointer flex-col overflow-hidden backdrop-blur-none transition-all duration-300 hover:-translate-y-1 hover:shadow-lg'
      )}
      onClick={onClick ?? (() => (window.location.href = `/team/${id}`))}
    >
      {canManageTeam && (
        <div
          className="absolute right-3 top-3 z-10 p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <TeamActions
            team={team}
            tournament={tournament}
            onAction={onAction}
            onEdit={onEdit}
          />
        </div>
      )}

      <div className="relative aspect-[16/9] w-full overflow-hidden">
        <AppImage
          src={logo || ''}
          alt={name}
          gradient
          fallbackText={name}
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />

        <div className="absolute inset-x-0 top-0 rounded-lg bg-gradient-to-b from-black/60 via-black/40 to-transparent p-2">
          <span className="inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-medium text-primary-700">
            Team
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col space-y-2 p-2">
        <h3 className="mb-2 text-base font-semibold leading-tight text-gray-900 sm:text-lg">
          {name}
        </h3>

        <div className="mt-2 space-y-2">
          {owner?.name && (
            <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 px-3 py-2">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-700">Owner</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {owner.name}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-primary-50/80 to-primary-100/80 px-3 py-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary-600" />
              <span className="text-sm text-gray-700">Players</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {playerCount}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-accent-green/5 to-accent-green/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-accent-green" />
              <span className="text-sm text-gray-700">Budget</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(budget || 0)}
            </span>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-accent-pink/5 to-accent-pink/10 px-3 py-2">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-accent-pink" />
              <span className="text-sm text-gray-700">Spent</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(spent || 0)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
